/**
 * SQZ inset detection — pure functions for opaque-edge scanning,
 * with an incremental scan when previous insets are known.
 */

export interface SqzInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

const ZERO_INSETS: SqzInsets = { top: 0, right: 0, bottom: 0, left: 0 };

/**
 * Compute the opaque-bar insets of a GFX snapshot.
 *
 * For each edge, scan consecutive fully-opaque rows/columns inward;
 * stop at the first row/column where any pixel has alpha !== 255.
 * When `lastInsets` is provided, probe bidirectionally from the previous
 * position so both grow (squeeze-in) and shrink (release) are handled
 * in O(|delta|).
 *
 * @returns Scene-pixel insets (integer counts on the GFX canvas grid).
 */
export function computeInsets(
  imageData: ImageData,
  lastInsets?: SqzInsets,
): SqzInsets {
  const { width: W, height: H, data } = imageData;
  if (W === 0 || H === 0) return ZERO_INSETS;

  const top = scanFromEdge(H, (i) => isRowFullyOpaque(data, W, i), lastInsets?.top);
  const bottom = scanFromEdge(
    H,
    (i) => isRowFullyOpaque(data, W, H - 1 - i),
    lastInsets?.bottom,
  );
  const left = scanFromEdge(W, (i) => isColFullyOpaque(data, W, H, i), lastInsets?.left);
  const right = scanFromEdge(
    W,
    (i) => isColFullyOpaque(data, W, H, W - 1 - i),
    lastInsets?.right,
  );

  return { top, right, bottom, left };
}

function isRowFullyOpaque(data: Uint8ClampedArray, width: number, row: number): boolean {
  const base = row * width * 4;
  for (let x = 0; x < width; x++) {
    if (data[base + x * 4 + 3] !== 255) return false;
  }
  return true;
}

function isColFullyOpaque(data: Uint8ClampedArray, width: number, height: number, col: number): boolean {
  for (let y = 0; y < height; y++) {
    if (data[(y * width + col) * 4 + 3] !== 255) return false;
  }
  return true;
}

/**
 * Bidirectional scan from one edge, starting at the previous inset `last`.
 *
 * Grow forward from `last`; if the bar did not grow, shrink inward until the
 * true boundary is reached. A stationary bar leaves `count` equal to `last`
 * in both loops.
 */
function scanFromEdge(
  max: number,
  opaque: (index: number) => boolean,
  last?: number,
): number {
  if (max <= 0) return 0;

  const base = Math.min(Math.max(0, last ?? 0), max);
  let count = base;

  while (count < max && opaque(count)) count++;
  if (count > base) return count;

  while (count > 0 && !opaque(count - 1)) count--;
  return count;
}
